import { Alert, AlertTitle } from '@mui/material'
import { format, startOfDay } from 'date-fns'
import type { FC } from 'react'
import { useContext, useState } from 'react'
import styled from 'styled-components'

import { Button, AlternateButton } from '@/components/DEPRECATED_UI/Button'
import SlideInSidebar from '@/components/DEPRECATED_UI/SlideInSidebar'
import { isNonProfitOrganisation } from '@/components/EntityPanels/OrganisationalDetails/utils'
import H2 from '@/components/H2'
import DateInput from '@/components/input/DateInput'
import TextInput from '@/components/input/TextInput'
import LoadingSpinner from '@/components/LoadingSpinner'
import Select from '@/components/Select'
import ctx from '@/context/AppContext'
import { useVerifyOrganisationMutation } from '@/lib/apollo/mutation/verify-organisation'
import { getErrorsFromGraphQLExtensions } from '@/lib/apollo/utils/getUpstreamErrors'
import type { ErrorContext } from '@/lib/apollo/utils/getUpstreamErrors'
import type {
  CompanyDetails,
  CompanyHouseCheck,
  CompanyType,
  OrganisationStatus,
} from '@/types/Activity'

const ContentContainer = styled.div`
  display: grid;
  gap: 20px;
  margin-top: 20px;
`

const VerificationOptions = styled.div`
  margin: 5px;

  div {
    margin-bottom: 5px;
  }
`

export interface Props {
  customerId: string
  subjectId: string
  company: CompanyDetails
  onClose: () => void
}

interface Fields {
  companyNumber: string | null
  companyHouseCheck: CompanyHouseCheck
  companyType: CompanyType
  organisationStatus: OrganisationStatus
  incorporationDate: Date | null
  filedAccount: Date | null
}

const VerifyOrganisationSidebar: FC<Props> = ({
  customerId,
  subjectId,
  company,
  onClose,
}) => {
  const [errors, setErrors] = useState<ErrorContext<string>[] | undefined>()
  const [fields, setFields] = useState<Fields>({
    companyHouseCheck: company.verification?.companyHouseCheck ?? 'NotVerified',
    companyType: company.verification?.companyType ?? 'PublicLimitedCompany',
    organisationStatus: company.verification?.organisationStatus ?? 'Active',
    incorporationDate: company.verification?.incorporationDate
      ? new Date(company.verification?.incorporationDate)
      : company.incorporationDate
        ? new Date(company.incorporationDate)
        : null,
    filedAccount: company.verification?.filedAccount
      ? new Date(company.verification?.filedAccount)
      : null,
    companyNumber: company.verification?.companyNumber ?? company.companyNumber ?? '',
  })

  const [verifyOrganisation, { loading, error, data }] = useVerifyOrganisationMutation()

  const { personId } = useContext(ctx)

  const shouldShowFiledAccounts = isNonProfitOrganisation(fields.companyType)

  const toDatePayload = (date: Date): string => format(startOfDay(date), 'yyyy-MM-dd')

  const isInvalidRequest =
    fields.companyHouseCheck === null ||
    fields.companyType === null ||
    fields.organisationStatus === null ||
    fields.incorporationDate === null ||
    fields.companyNumber === null ||
    fields.companyNumber?.trim() === '' ||
    (shouldShowFiledAccounts && fields.filedAccount === null)

  const onConfirm = () => {
    if (isInvalidRequest) {
      return
    }

    verifyOrganisation({
      variables: {
        customerId,
        subjectId,
        userId: personId ?? '',
        request: {
          companyNumber: fields.companyNumber!,
          companyHouseCheck: fields.companyHouseCheck,
          companyType: fields.companyType,
          organisationStatus: fields.organisationStatus,
          incorporationDate: toDatePayload(fields.incorporationDate!),
          filedAccount: fields.filedAccount ? toDatePayload(fields.filedAccount) : null,
        },
      },
      onError: error => {
        setErrors(getErrorsFromGraphQLExtensions(error))
      },
    })
  }

  return (
    <SlideInSidebar
      onClose={onClose}
      header={<H2>Verify organisation</H2>}
      content={
        <ContentContainer>
          {data !== undefined || loading ? (
            <LoadingSpinner />
          ) : (
            <div>
              <TextInput
                label="Company Number"
                onChange={e => setFields({ ...fields, companyNumber: e.target.value })}
                type="text"
                value={fields.companyNumber}
                required
              />
              <VerificationOptions>
                <Select<CompanyHouseCheck>
                  onSelect={e => setFields({ ...fields, companyHouseCheck: e })}
                  options={[
                    { label: 'Verified', value: 'Verified' },
                    { label: 'Not Verified', value: 'NotVerified' },
                    { label: 'Overseas', value: 'Overseas' },
                  ]}
                  selectedValue={fields.companyHouseCheck ?? 'NotVerified'}
                  label="Company House Check"
                />
                <Select<CompanyType>
                  onSelect={e => {
                    setFields(prevFields => ({
                      ...prevFields,
                      companyType: e,
                      filedAccount: !shouldShowFiledAccounts
                        ? null
                        : prevFields.filedAccount,
                    }))
                  }}
                  options={[
                    { label: 'Public Limited Company', value: 'PublicLimitedCompany' },
                    { label: 'Private Limited Company', value: 'PrivateLimitedCompany' },
                    {
                      label: 'Limited Liability Partnership',
                      value: 'LimitedLiabilityPartnership',
                    },
                    {
                      label: 'Charitable Incorporated Organisation',
                      value: 'CharitableIncorporatedOrganisation',
                    },
                    {
                      label:
                        'Cooperative And Community Benefit Society With Charitable Status',
                      value: 'CooperativeAndCommunityBenefitSocietyWithCharitableStatus',
                    },
                    {
                      label:
                        'Overseas Company Required To Publish Accounts Under Parent Law',
                      value: 'OverseasCompanyRequiredToPublishAccountsUnderParentLaw',
                    },
                    {
                      label: 'Private Limited Company',
                      value: 'PrivateLimitedCompany',
                    },
                    {
                      label: 'Private Limited Company By Guarantee Without Share Capital',
                      value: 'PrivateLimitedCompanyByGuaranteeWithoutShareCapital',
                    },
                    {
                      label:
                        'Private Limited Company By Guarantee Without Share Capital Use Of Limited Exemption',
                      value:
                        'PrivateLimitedCompanyByGuaranteeWithoutShareCapitalUseOfLimitedExemption',
                    },
                  ]}
                  selectedValue={fields.companyType ?? 'PublicLimitedCompany'}
                  label="Company Type"
                />
                <Select<OrganisationStatus>
                  onSelect={e => setFields({ ...fields, organisationStatus: e })}
                  options={[
                    { label: 'Active', value: 'Active' },
                    { label: 'Administration', value: 'Administration' },
                    { label: 'Dissolved', value: 'Dissolved' },
                    { label: 'Dormant', value: 'Dormant' },
                    { label: 'Insolvent', value: 'Insolvent' },
                    { label: 'Liquidation', value: 'Liquidation' },
                  ]}
                  selectedValue={fields.organisationStatus ?? 'Active'}
                  label="Organisation Status"
                />
                <DateInput
                  date={fields.incorporationDate}
                  setDate={e => setFields({ ...fields, incorporationDate: e })}
                  label="Incorporation Date"
                />
                {shouldShowFiledAccounts && (
                  <DateInput
                    date={fields.filedAccount}
                    setDate={e => setFields({ ...fields, filedAccount: e })}
                    label="Filed Accounts"
                  />
                )}
              </VerificationOptions>
              {errors && (
                <Alert severity="error">
                  <AlertTitle>
                    There was an error submitting the information provided.
                  </AlertTitle>
                  {errors !== undefined && errors.length > 0 ? (
                    <ol>
                      {errors.map(({ description, reason }, i) => (
                        <li key={i}>
                          {description} - ({reason})
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <span>Please try again</span>
                  )}
                </Alert>
              )}
            </div>
          )}
        </ContentContainer>
      }
      footer={
        <>
          {error && <div>An unexpected error occurred. {error?.message}</div>}
          <div className="side-by-side-flex-end">
            <AlternateButton onClick={onClose}>Cancel</AlternateButton>
            <Button disabled={isInvalidRequest || loading} onClick={onConfirm}>
              Confirm
            </Button>
          </div>
        </>
      }
    />
  )
}

export default VerifyOrganisationSidebar
